import {
  Box,
  Button,
  Collapse,
  CollapseHeader,
  CollapsePanel,
  Text,
  openToast,
} from "@vinomofo/components";
import { Field, Form, Formik } from "formik";
import { useCallback, useState } from "react";

import { SuccessToast } from "../toast";
import { TextField } from "../form-inputs/FormFields";
import { trackEvent } from "../../utils/track-event";
import useAuth from "../../hooks/use-auth";
import { useCart } from "../../hooks/use-cart";
import { useLocation } from "@reach/router";
import useOneClickCheckout from "../../hooks/use-one-click-checkout";
import { trackCartCouponEvent } from "../../utils/event-tracking";

export const AddCouponForm = (): JSX.Element => {
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("returnTo");
  const { cart, isLoading, error, addCoupon } = useCart();
  const { coupons } = cart;
  const {
    isProcessing,
    isBuying,
    isRedirecting: oneClickCheckoutRedirecting,
  } = useOneClickCheckout();
  const { login, join } = useAuth();
  const [isFormVisible, setIsFormVisible] = useState(false);

  const toggleForm = () => {
    setIsFormVisible((prev) => !prev);
  };

  const handleLogin = useCallback(async () => {
    trackEvent("Login Clicked - Error", {
      button: "Login",
      location: returnTo ? returnTo : "/",
    });
    login({ returnTo: returnTo ? returnTo : "/" });
  }, [login]);

  const handleSignUp = useCallback(async () => {
    trackEvent("Join Clicked - Error", {
      button: "Join",
      location: returnTo ? returnTo : "/",
    });
    join({ returnTo: returnTo ? returnTo : "/" });
  }, [join]);

  // const hasError = error && error?.stack === "coupon" && !isLoading;
  const isDisabled = isBuying || isProcessing || oneClickCheckoutRedirecting;

  return (
    <Collapse
      isOpen={isFormVisible}
      onClick={toggleForm}
      width="100%"
      px={[3]}
      pt={["18px"]}
      pb={[2]}
    >
      <CollapseHeader
        iconSize="15px"
        paddingTop={0}
        px={0}
        disabled={isDisabled}
      >
        <Text
          fontSize={["15px"]}
          fontWeight="500"
          color="primary"
          data-testid="collapse-coupon-code"
        >
          Add a discount/coupon code
        </Text>
      </CollapseHeader>
      <CollapsePanel px={0}>
        <Formik
          initialValues={{ couponCode: "" }}
          onSubmit={async (values, { resetForm }) => {
            trackCartCouponEvent(
              "Coupon Entered",
              location.pathname,
              cart,
              values?.couponCode
            );
            const addCouponResponse = await addCoupon(values?.couponCode);
            const responseCoupon = addCouponResponse?.coupons?.[0];
            if (
              coupons.length > 0 &&
              addCouponResponse?.id &&
              addCouponResponse?.coupons?.length > 0 &&
              coupons[0].code !== responseCoupon?.code
            ) {
              openToast(
                <SuccessToast message="Your coupon has been updated." />
              );
            } else {
              if (responseCoupon)
                trackCartCouponEvent(
                  "Coupon Applied",
                  location.pathname,
                  cart,
                  responseCoupon?.code,
                  responseCoupon?.discounted_amount
                );
            }
            resetForm({});
          }}
        >
          {({ isValid, dirty, isSubmitting }) => (
            <Form>
              <Box width="100%">
                {error && !isLoading && (
                  <>
                    {error?.stack === "coupon" && (
                      <Box mb={3}>
                        <Text color="deal" fontSize={["14px"]}>
                          {error?.message}
                        </Text>
                      </Box>
                    )}
                    {error?.stack === "guest-coupon" && (
                      <Box mb={3}>
                        <Text color="deal" fontSize={["14px"]}>
                          Hmmm. You need to be signed-in to try this coupon. Why
                          not{" "}
                          <Button
                            onClick={handleLogin}
                            color="greenLantern"
                            fontFamily={"normal"}
                            fontSize={1}
                            fontWeight="400"
                            type="button"
                          >
                            login
                          </Button>
                          {"/"}
                          <Button
                            onClick={handleSignUp}
                            color="greenLantern"
                            fontFamily={"normal"}
                            fontSize={[0, 1]}
                            fontWeight="400"
                            type="button"
                          >
                            join
                          </Button>{" "}
                          and try again?
                        </Text>
                      </Box>
                    )}
                  </>
                )}
                <Field
                  label="Discount/Coupon code"
                  name="couponCode"
                  placeholder="Enter code"
                  component={TextField}
                  disabled={isSubmitting}
                />
                <Box display="flex" width="100%" justifyContent="flex-end">
                  <Button
                    variant="primary"
                    disabled={!dirty || !isValid || isSubmitting}
                    width="100%"
                  >
                    Apply
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </CollapsePanel>
    </Collapse>
  );
};
